import { Chip, cn } from "@au-schein-de/au-ui-library";
import { createLogger } from "@dransay/logger/browser";
import type { ReactNode } from "react";

const logger = createLogger({ service: "platform" });

import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router";
import type { Route } from "../../../../../.react-router/types/app/+types/root";
import {
	FILTER_CATEGORY_TRANSLATION_KEYS,
	FILTER_VALUES_TRANSLATION_KEYS,
} from "../constants";
import type { FiltersState, RemoveFilterFn } from "../types";

type FilterChipProps = {
	category: keyof FiltersState;
	value: string;
	onRemove: RemoveFilterFn;
	showCategory?: boolean;
	capitalize?: boolean;
	icon?: ReactNode;
	color?: string;
};

export function FilterChip({
	category,
	value,
	onRemove,
	showCategory = false,
	capitalize = true,
	icon,
	color,
}: FilterChipProps) {
	const { t } = useTranslation();
	const { t: tProduct, i18n } = useTranslation("product");

	const filterValueTranslationKeys = useMemo(() => {
		const filterValueTranslationsKeys = {
			...FILTER_VALUES_TRANSLATION_KEYS,
		};
		filterValueTranslationsKeys.topProducersLowPrice += ".withFeatureFlag";
		return filterValueTranslationsKeys;
	}, []);

	const categoryTranslation =
		category in FILTER_CATEGORY_TRANSLATION_KEYS
			? t(
					FILTER_CATEGORY_TRANSLATION_KEYS[
						category as keyof typeof FILTER_CATEGORY_TRANSLATION_KEYS
					],
				)
			: category.toString();

	const valueTranslationKey = `${filterValueTranslationKeys[category as keyof typeof filterValueTranslationKeys]}.${value}`;
	const valueTranslationExists = i18n.exists(valueTranslationKey, {
		ns: "product",
	});

	useEffect(() => {
		if (category in filterValueTranslationKeys && !valueTranslationExists) {
			logger.warn({ key: valueTranslationKey }, "Missing translation");
		}
	}, [
		category,
		valueTranslationExists,
		valueTranslationKey,
		filterValueTranslationKeys,
	]);

	const valueTranslation = valueTranslationExists
		? // @ts-ignore: dynamic key, verified above
			(tProduct(valueTranslationKey) as string)
		: value;

	return (
		<Chip
			data-testid={`filter-chip-${category}`}
			onRemove={() => onRemove(category, value)}
			className={cn("whitespace-nowrap flex-shrink-0", {
				capitalize: capitalize,
			})}
		>
			{icon && <span className="mr-1.5">{icon}</span>}
			{color && (
				<span
					className="mr-1.5 inline-block size-2 rounded-full"
					style={{ backgroundColor: color }}
				/>
			)}
			{showCategory
				? `${categoryTranslation}: ${valueTranslation}`
				: valueTranslation}
		</Chip>
	);
}
